/**
 * The auditor landing page (E2.1 design spec section 4, reworked by the
 * round-3 owner review in section 11, layout swap + skeletons by owner
 * ruling 2026-07-15): "is the money growing, and where", answered in
 * reading order -- the financial-statement card (11.4, replaced the
 * 6-KPI-card row), government take card (demoted, 11.4), ranked shops
 * table (11.7), money-over-time chart (11.5; the owner moved the table
 * above the chart after browser review). The page-level "Loading
 * dashboard..." gate is gone: the header + period controls render
 * immediately and each card shows its own REPRESENTATIVE first-load
 * skeleton (dashboardSkeletons.tsx) until its data lands, then pops in
 * independently -- later reloads keep showing the last data as before.
 * Composes GET /reports/summary
 * (current + previous period, for the delta chips -- when the selection IS
 * the current month BOTH chip sides are capped through yesterday while the
 * headline figures stay uncapped MTD),
 * GET /reports/timeseries and GET /reports/by-shop. Every call carries the
 * population selection as report_type (ROADMAP:40) -- Live only by default,
 * widened by the reports:write-gated toggles; the server enforces it.
 *
 * The chart's year is independent of the KPI/table period (spec 4.5: "The
 * chart ALWAYS shows the selected year month-by-month"); its shop filter is
 * populated from the by-shop response already fetched elsewhere on the page.
 *
 * Owner privacy ruling (2026-07-14): GET /reports/by-shop now ALSO requires
 * shops:read (server-side, matching grouped GET /reports/timeseries). A
 * session without shops:read gets ZERO shop-roster exposure -- the same
 * zero-trace pattern as revenue below: fetchByShop never calls
 * api.reports.byShop at all for that session, the Shops table doesn't
 * render, and MoneyChart's shopFilterEnabled prop hides its shop
 * multi-select (and, with it, the metric select -- compare mode is
 * unreachable with no shop to pick). The currency selector still works for
 * that session because collectCurrencies unions summary/timeseries/revenue
 * too -- byShop.shops resolves to the empty EMPTY_BY_SHOP fallback and
 * simply contributes nothing to that union, no special-casing needed.
 *
 * PERF (spec 11.5): the chart's shop/metric selection must refetch ONLY the
 * timeseries call. fetchTimeseries is the only useCallback below whose
 * dependency array includes chartShopIds -- summary/by-shop/revenue's
 * fetchers depend only on [api, year, effectiveMonth, ...], so useLoadState
 * never re-runs them when the shop filter changes. The metric selector goes
 * further and causes NO refetch at all: group_by=shop already returns every
 * metric, so switching Gross sales/Payout/Net profit is a pure client-side
 * re-derivation in chartHelpers.buildShopCompareSeries.
 */
import { useEffect, useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { useAuth } from '../../auth/AuthContext.tsx';
import { LoadErrorAlert } from '../../components/LoadErrorAlert.tsx';
import type { LoadState } from '../../hooks/useLoadState.ts';
import { palette } from '../../theme.ts';
import { CHART_MONTH_LABELS, type ChartMetric, isGroupedTimeseries, timeseriesCurrencyCodes, type ShopOption } from './chartHelpers.ts';
import { deriveChipBasis } from './chipBasis.ts';
import {
  collectCurrencies,
  effectiveCurrency,
  pickCurrencySummary,
  pickRevenueTotal,
  rankShopsByGross,
  revenueRowsByShopId,
} from './dashboardHelpers.ts';
import { GovernmentTakeCard } from './GovernmentTakeCard.tsx';
import { MoneyChart } from './MoneyChart.tsx';
import { PeriodControls } from './PeriodControls.tsx';
import { computeComparisonContext, defaultPeriod, previousPeriod } from './periodState.ts';
import { ReportTypeToggles } from './ReportTypeToggles.tsx';
import { DEFAULT_REPORT_TYPES, isDefaultReportTypes, loadReportTypes, saveReportTypes } from './reportTypeStorage.ts';
import { RevenueCard } from './RevenueCard.tsx';
import { ShopsTable } from './ShopsTable.tsx';
import { StatementCard } from './StatementCard.tsx';
import { EMPTY_BY_SHOP, EMPTY_REVENUE, EMPTY_SUMMARY, useDashboardFetchers } from './useDashboardFetchers.ts';

/** The dashboard's headline error banner shows the first error in priority
 *  order: current summary, previous summary, capped current (chip-basis)
 *  summary, then revenue (only when the session can see it at all).
 *  Extracted from DashboardPage's render to keep it a single flat statement
 *  rather than a nested ternary (SonarQube S3358). */
function firstDashboardError(
  currentState: LoadState,
  previousState: LoadState,
  cappedCurrentState: LoadState,
  canReadRevenue: boolean,
  revenueState: LoadState,
): string | null {
  if (currentState.kind === 'error') return currentState.message;
  if (previousState.kind === 'error') return previousState.message;
  if (cappedCurrentState.kind === 'error') return cappedCurrentState.message;
  if (canReadRevenue && revenueState.kind === 'error') return revenueState.message;
  return null;
}

/** The page subtitle: the whole year, or "<Month> <year>", with a
 *  "(month to date)" suffix when the selected month is still in progress.
 *  Extracted to keep DashboardPage's render free of nested ternaries
 *  (SonarQube S3358). */
function periodLabelText(wholeYear: boolean, year: number, month: number, isMtd: boolean): string {
  if (wholeYear) return `${year}`;
  const suffix = isMtd ? ' (month to date)' : '';
  return `${CHART_MONTH_LABELS[month - 1]} ${year}${suffix}`;
}

export function DashboardPage(): React.JSX.Element {
  const { api, hasPermission } = useAuth();
  const canReadRevenue = hasPermission('revenue:read');
  // Gates the by-shop fetch itself (owner privacy ruling 2026-07-14, mirrors
  // canReadRevenue's zero-trace pattern below), the Shops table's rendering,
  // the chart's shop multi-select, AND (unchanged from before) the
  // shop-name-links-to-/shops/:id gate on that table.
  const canReadShops = hasPermission('shops:read');
  // Daily-detail expansion is internal-staff convenience only (owner ruling
  // 2026-08-12) -- NOT a security boundary; reports:read still serves daily
  // rows elsewhere by design. It only keeps the affordance off External's screen.
  const canExpandDetail = hasPermission('reports:write');
  // The rich view (gross/net profit rows, payout ratio, delta chips, money
  // chart) is internal-staff framing, NOT a security boundary (owner ruling
  // 2026-08-12): net profit is derivable arithmetic on figures External
  // legitimately receives from /reports/summary; the only true secret, 1NL
  // revenue, is already server-gated behind revenue:read.
  const showInternalDetail = hasPermission('reports:write');
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));

  const start = defaultPeriod();
  const [year, setYear] = useState(start.year);
  const [month, setMonth] = useState(start.month ?? 1);
  const [wholeYear, setWholeYear] = useState(false);
  const [chartShopIds, setChartShopIds] = useState<number[]>([]);
  const [chartMetric, setChartMetric] = useState<ChartMetric>('gross_sales');
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  // ROADMAP:40 population selection, persisted across sessions.
  const [reportTypes, setReportTypes] = useState<string[]>(loadReportTypes);

  function changeReportTypes(next: string[]): void {
    setReportTypes(next);
    saveReportTypes(next);
  }

  // The selection persists in localStorage, so a non-writer arriving with a
  // stored demo/training population must be reset (mirrors DashboardShell's
  // currency escape trap); the fetchers clamp to live regardless.
  useEffect(() => {
    if (!showInternalDetail && !isDefaultReportTypes(reportTypes)) {
      setReportTypes([...DEFAULT_REPORT_TYPES]);
      saveReportTypes(DEFAULT_REPORT_TYPES);
    }
  }, [showInternalDetail, reportTypes]);

  const effectiveMonth = wholeYear ? undefined : month;
  const period = { year, month: wholeYear ? null : month };
  const comparisonContext = computeComparisonContext(period);
  const prev = previousPeriod(period);

  const fetchers = useDashboardFetchers({
    api,
    year,
    effectiveMonth,
    prev,
    toDay: comparisonContext.toDay,
    comparable: comparisonContext.comparable,
    chartShopIds,
    reportTypes,
    canReadShops,
    canReadRevenue,
    showInternalDetail,
  });
  const {
    currentSummary,
    currentState,
    reloadCurrent,
    previousSummary,
    previousState,
    reloadPrevious,
    timeseriesResult,
    timeseriesState,
    reloadTimeseries,
    byShop,
    byShopState,
    reloadByShop,
    revenue,
    revenueState,
    reloadRevenue,
    previousRevenue,
    reloadPreviousRevenue,
    cappedCurrentState,
    reloadCappedCurrent,
    reloadCappedCurrentRevenue,
  } = fetchers;

  const currencies = useMemo(
    () =>
      collectCurrencies(
        currentSummary.by_currency.map((c) => c.currency_code),
        previousSummary.by_currency.map((c) => c.currency_code),
        timeseriesCurrencyCodes(timeseriesResult),
        byShop.shops.flatMap((s) => s.by_currency.map((c) => c.currency_code)),
        revenue.totals.map((t) => t.currency_code),
        previousRevenue.totals.map((t) => t.currency_code),
      ),
    [currentSummary, previousSummary, timeseriesResult, byShop, revenue, previousRevenue],
  );

  const currencyCode = effectiveCurrency(selectedCurrency, currencies);
  if (currencyCode !== selectedCurrency && currencyCode !== null) {
    // Keep selection in sync with newly loaded data without an extra effect
    // round-trip -- safe to call during render (React de-dupes identical
    // state, and this only fires when the derived value actually changes).
    setSelectedCurrency(currencyCode);
  }

  const shopOptions: ShopOption[] = useMemo(
    () => byShop.shops.map((s) => ({ id: s.shop_id, label: `${s.shop_name} (${s.shop_code})` })),
    [byShop],
  );

  const currentForCurrency = pickCurrencySummary(currentSummary.by_currency, currencyCode ?? '');
  const previousForCurrency = pickCurrencySummary(previousSummary.by_currency, currencyCode ?? '');
  const rankedShops = rankShopsByGross(byShop.shops, currencyCode ?? '');

  const currentRevenueTotal = pickRevenueTotal(revenue.totals, currencyCode ?? '');
  const previousRevenueTotal = pickRevenueTotal(previousRevenue.totals, currencyCode ?? '');
  const revenueByShopId = useMemo(() => revenueRowsByShopId(revenue.by_shop), [revenue]);

  const { chipCurrentForCurrency, chipRevenueTotal, showDeltas, showRevenueDeltas } = deriveChipBasis({
    ...fetchers,
    comparisonContext,
    showInternalDetail,
    currencyCode,
  });

  const timeseriesIsGrouped = isGroupedTimeseries(timeseriesResult);
  const chartMonths = timeseriesIsGrouped ? [] : timeseriesResult.months;
  const chartGroupedShops = timeseriesIsGrouped ? timeseriesResult.shops : [];

  const firstError = firstDashboardError(currentState, previousState, cappedCurrentState, canReadRevenue, revenueState);

  function retryAll(): void {
    void reloadCurrent();
    void reloadPrevious();
    void reloadTimeseries();
    void reloadByShop();
    void reloadRevenue();
    void reloadPreviousRevenue();
    void reloadCappedCurrent();
    void reloadCappedCurrentRevenue();
  }

  // Per-card FIRST-load-only skeleton signals (owner ruling 2026-07-15).
  // useLoadState's `data` stays at its initial value until the first load
  // resolves (see its docstring) -- REFERENCE equality against each
  // fetcher's exported EMPTY_* initial constant is therefore a reliable
  // "first load still in flight" signal, distinct from a later
  // period-change reload (which keeps showing the prior data rather than
  // flashing a skeleton). Reference, not emptiness: a genuinely-empty
  // server response is a fresh object, so the flag goes false forever after
  // the first load even for a zero-shop / zero-revenue tenant, where an
  // emptiness check would re-flash skeletons on every period change.
  const statementFirstLoad = currentState.kind === 'loading' && currentSummary === EMPTY_SUMMARY;
  const shopsFirstLoad = byShopState.kind === 'loading' && byShop === EMPTY_BY_SHOP;
  const revenueFirstLoad = revenueState.kind === 'loading' && revenue === EMPTY_REVENUE;

  const periodLabel = periodLabelText(wholeYear, year, month, comparisonContext.isMtd);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5">Dashboard</Typography>
          <Typography variant="body2" sx={{ color: palette.textSecondary, mt: 0.5 }}>
            {periodLabel}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <PeriodControls
            year={year}
            month={month}
            wholeYear={wholeYear}
            onYearChange={setYear}
            onMonthChange={setMonth}
            onWholeYearChange={setWholeYear}
            currencies={currencies}
            currencySelectorEnabled={showInternalDetail}
            currencyCode={currencyCode}
            onCurrencyChange={setSelectedCurrency}
          />
          {showInternalDetail && <ReportTypeToggles value={reportTypes} onChange={changeReportTypes} />}
        </Box>
      </Box>

      {firstError && <LoadErrorAlert message={firstError} />}
      {firstError && (
        <Box>
          <Typography
            component="button"
            onClick={retryAll}
            sx={{ font: 'inherit', color: 'primary.main', fontWeight: 700, cursor: 'pointer', bgcolor: 'transparent', border: 'none', p: 0 }}
          >
            Retry
          </Typography>
        </Box>
      )}

      {!firstError && (
        <>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'stretch' }}>
            {/* Pixel flex-bases only at md+: stacked at xs they would be read as HEIGHTS on the flipped main axis. */}
            <Box sx={{ flex: { xs: '1 1 auto', md: '2 1 480px' }, minWidth: 0, width: '100%' }}>
              <StatementCard
                currentSummary={currentForCurrency}
                previousSummary={previousForCurrency}
                chipCurrentSummary={chipCurrentForCurrency}
                currencyCode={currencyCode}
                comparisonContext={comparisonContext}
                loading={statementFirstLoad}
                showDeltas={showDeltas}
                detailed={showInternalDetail}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 auto', md: '1 1 320px' }, minWidth: 0, width: { xs: '100%', md: 'auto' }, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {canReadRevenue && (
                <RevenueCard
                  revenueMinor={currentRevenueTotal.revenue_minor}
                  previousRevenueMinor={previousRevenueTotal.revenue_minor}
                  chipRevenueMinor={chipRevenueTotal.revenue_minor}
                  commissionMinor={currentRevenueTotal.commission_minor}
                  licenceFeeMinor={currentRevenueTotal.licence_fee_minor}
                  commissionRateBp={revenue.commission_rate_bp}
                  deviceMonthlyFeeMinor={revenue.device_monthly_fee_minor}
                  currencyCode={currencyCode}
                  comparisonContext={comparisonContext}
                  loading={revenueFirstLoad}
                  showDeltas={showRevenueDeltas}
                />
              )}
              <GovernmentTakeCard
                taxTotalMinor={currentForCurrency.tax_total_minor}
                previousTaxTotalMinor={previousForCurrency.tax_total_minor}
                chipTaxTotalMinor={chipCurrentForCurrency.tax_total_minor}
                taxSplits={currentForCurrency.tax_splits}
                currencyCode={currencyCode}
                comparisonContext={comparisonContext}
                loading={statementFirstLoad}
                showDeltas={showDeltas}
              />
            </Box>
          </Box>

          {canReadShops && (
            <ShopsTable
              rows={rankedShops}
              currencyCode={currencyCode}
              compact={compact}
              loading={shopsFirstLoad}
              error={byShopState.kind === 'error' ? byShopState.message : null}
              showRevenue={canReadRevenue}
              revenueByShopId={canReadRevenue ? revenueByShopId : null}
              defaultCommissionRateBp={revenue.commission_rate_bp}
              defaultDeviceFeeMinor={revenue.device_monthly_fee_minor}
              year={year}
              month={wholeYear ? null : month}
              canExpandDetail={canExpandDetail}
              shopLinkEnabled={canReadShops}
            />
          )}

          {showInternalDetail && (
            <MoneyChart
              year={year}
              months={chartMonths}
              groupedShops={chartGroupedShops}
              currencyCode={currencyCode}
              shopOptions={shopOptions}
              selectedShopIds={chartShopIds}
              onShopIdsChange={setChartShopIds}
              metric={chartMetric}
              onMetricChange={setChartMetric}
              shopFilterEnabled={canReadShops}
              canReadRevenue={canReadRevenue}
              shopSyncTimestamps={byShop.shops.map((s) => s.last_sync_at)}
              compact={compact}
              loading={timeseriesState.kind === 'loading'}
              error={timeseriesState.kind === 'error' ? timeseriesState.message : null}
            />
          )}
        </>
      )}
    </Box>
  );
}

export default DashboardPage;
